import type { Game, GameData } from 'shared/modelTypes/index';
import { MessageCircleQuestion, Palette, Pen, RectangleVertical } from 'lucide-static';
import { Icon } from './Icon';
import { Button } from './Button';
import { Avatar } from './Avatar';
import { unreachable } from 'shared/util';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import type { PersonalityColor } from 'shared/modelTypes/team-kleuren';
import { Tab, TabView } from './TabView';

type GameViewProps = {
	game: Game;
};
export function GameView({ game }: GameViewProps) {
	const view: HTMLElement = (
		<div class="mx-auto rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800">
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-4">
					<Avatar src={`/images/${game.kind}.jpg`} alt="game icon" />
					<h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">{game.name ?? game.kind}</h2>
				</div>
			</div>

			{game.kind === 'team-kleuren-spel' && <TeamKleurenDataView data={game.data} />}
			{game.kind === 'kwaliteiten-jacht' && <KwaliteitenJachtDataView data={game.data} />}
			<hr class="my-3 dark:border-gray-700" />
			<div class="flex gap-4">
				<Button mode="secondary" href={'/dashboard/games/edit/' + game.kind}>
					Edit
					<Icon icon={Pen} class="size-3" />
				</Button>
			</div>
		</div>
	);

	return view;
}

function classFromColor(color: PersonalityColor) {
	// prettier-ignore
	switch(color) {
		case 'blue': return 'border-blue-600';break;
		case 'red': return 'border-red-600';break;
		case 'green': return 'border-green-600 ';break;
		case 'yellow': return 'border-yellow-600';break;
		default: unreachable(color)
	}
}
function TeamKleurenDataView({ data }: { data: GameData<'team-kleuren-spel'> }) {
	return (
		<TabView class="overflow-hidden rounded-md border border-gray-200">
			<Tab name="cards" label="Kaarten" icon={RectangleVertical}>
				<div class="grid grid-cols-2 gap-4">
					<div>
						<h3 class="text-lg font-semibold">Good cards</h3>
						<div class="grid gap-2">
							{data.goodCards.map((card) => (
								<div
									key={card.name}
									class={`rounded-xl border-4 p-1 shadow-sm transition-all ${classFromColor(card.color)}`}
								>
									{card.name}
								</div>
							))}
						</div>
					</div>
					<div>
						<h3 class="text-lg font-semibold">Bad cards </h3>
						<div class="grid gap-2">
							{data.badCards.map((card) => (
								<div class={`rounded-xl border-4 p-1 shadow-sm transition-all ${classFromColor(card.color)}`}>
									{card.name}
								</div>
							))}
						</div>
					</div>
				</div>
			</Tab>
			<Tab name="color-questions" label="Kleur vragen" icon={Palette}>
				<div class="grid gap-2">
					<h3 class="text-lg font-semibold">Kleur vragen</h3>
					{data.colorQuizQuestions.map((c) => (
						<div class={`rounded-xl border-4 p-1 shadow-sm transition-all ${classFromColor(c.color)}`}>{c.question}</div>
					))}
				</div>
			</Tab>
			<Tab name="text-questions" label="Text vragen" icon={MessageCircleQuestion}>
				<div class="flex w-full flex-col">
					<h3 class="mb-4 font-bold text-gray-700">Text questions</h3>
					<div class="space-y-6">
						{data.textQuizzes.map((card) => (
							<div class="relative rounded-lg border bg-white p-4 shadow-sm">
								<span class="absolute right-4 top-4 rounded-full bg-green-100 px-3 py-1 text-sm text-green-800">
									+{card.correctAnswer}
								</span>
								<p class="mb-4 text-gray-800">{card.question}</p>
								<div class="grid grid-cols-2 gap-4">
									{card.answers.map((answer, index) => (
										<div key={index} class="rounded-md bg-gray-100 p-2 text-sm text-gray-700">
											{answer}
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			</Tab>
		</TabView>
	);
}

function KwaliteitenJachtDataView({ data }: { data: GameData<'kwaliteiten-jacht'> }) {
	return (
		<div class="mt-8 flex max-w-full overflow-auto">
			<Table>
				<thead>
					<TrHead>
						<Th>Row</Th>
						{data.startingCards.map((_, playerIndex) => (
							<Th key={playerIndex}>Player {playerIndex + 1}</Th>
						))}
					</TrHead>
				</thead>
				<tbody>
					{data.startingCards[0].map((_, rowIndex) => (
						<TrBody key={rowIndex}>
							<Td>{rowIndex + 1}</Td>
							{data.startingCards.map((player, playerIndex) => (
								<Td key={playerIndex} class="border-gray-200 p-3 text-gray-500 dark:text-gray-400">
									{player[rowIndex]?.name ?? '-'}
								</Td>
							))}
						</TrBody>
					))}
				</tbody>
			</Table>
		</div>
	);
}
